import React, { useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material';
import { Typography, Divider, Popper, IconButton, makeStyles, useMediaQuery } from '@ellucian/react-design-system/core';
import { useParams } from 'react-router-dom';
import { useIntl, FormattedMessage } from 'react-intl';
import SectionLabel from './SectionLabel';
import { CONSTANTS } from '../Helpers/Configuration';
import Icon from '@ellucian/ds-icons/lib/es6/Icon';
import useApplicationForm from './useApplicationForm';
import RepeatingSection from './RepeatingSection';
import QuestionGroups from './QuestionGroups';
import ConditionalLogicCheck from './ConditionalLogicCheck';
import '../Admin/AdminControls/FormReactQuill.css';
import { spacing60, spacing40 } from '@ellucian/react-design-system/core/styles/tokens';
import classnames from 'classnames';
import HtmlFormattedMessage from '../HtmlFormattedMessage/HtmlFormattedMessage';

const useStyles = makeStyles(theme => ({
    hide: {
        display: 'none',
    },
    pageDivider: {
        marginBottom: [0, '!important'],
    },
    pageHeading: {
        display: 'flex',
        alignItems: 'baseline',
        paddingTop: spacing60,
        '&.short': {
            paddingTop: spacing40,
        },
        '&.noGap': {
            paddingTop: 'unset',
        },
    },
    sectionHeading: {
        display: 'flex',
        alignItems: 'baseline',
        paddingTop: spacing60,
        '&.short': {
            paddingTop: spacing40,
        },
    },
    [theme.breakpoints.down('sm')]: {
        horizontal: {
            gridAutoFlow: 'row',
        },
    },
}));
const ApplicationFormPage = props => {
    const intl = useIntl();
    const {
        page,
    } = props;
    const { inquiryMode, supplementalFormMode } = useApplicationForm();
    const classes = useStyles();
    const theme = useTheme();
    const pageTitleRef = useRef(null);
    const routeParams = useParams();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [openPageHelp, setOpenPageHelp] = useState(false);
    const [pageHelpAnchorEl, setPageHelpAnchorEl] = useState(null);
    const [openSectionHelp, setOpenSectionHelp] = useState(false);
    const [sectionHelpAnchorEl, setSectionHelpAnchorEl] = useState(null);
    const handlePageHelpClick = event => {
        const { currentTarget } = event;
        setPageHelpAnchorEl(currentTarget);
        setOpenPageHelp(!openPageHelp);
    };
    const handleSectionHelpClick = event => {
        const { currentTarget } = event;
        setSectionHelpAnchorEl(currentTarget);
        setOpenSectionHelp(!openSectionHelp);
    };
    const rootClassName = (supplementalFormMode || routeParams.pageId === page.id) ? null : classes.hide;
    const pageHeadingClassName = useMemo(() => {
        return classnames(classes.pageHeading, {
            short: inquiryMode,
            noGap: supplementalFormMode,
        }, [classes, inquiryMode, supplementalFormMode]);
    });
    const sectionHeadingClassName = useMemo(() => {
        return classnames(classes.sectionHeading, {
            short: inquiryMode || supplementalFormMode,
        }, [classes, inquiryMode, supplementalFormMode]);
    });

    return (
        <div className={rootClassName}>
            <main id='application-form-page'>
                <div>
                    <div className={pageHeadingClassName}>
                        <Typography 
                            variant="h1" 
                            id="page-title" 
                            ref={pageTitleRef} tabIndex="-1"
                        >
                            {intl.formatMessage({ id: page.titleId })}
                        </Typography>
                        {page.showHelp && page.helpId &&
                        <>
                            <IconButton
                                aria-label={intl.formatMessage({ id: 'helpText' })}
                                color="gray"
                                onClick={handlePageHelpClick}
                            >
                                <Icon name="help" />
                            </IconButton>
                            <Popper
                                anchorEl={pageHelpAnchorEl}
                                arrow
                                onClickAway={() => setOpenPageHelp(false)}
                                open={openPageHelp}
                                placement={isMobile ? 'bottom' : 'right'}
                                text={intl.formatMessage({ id: page.helpId })}
                                transition={false}
                            />
                        </>
                        }
                    </div>
                    <div>
                        {
                            page.showDescription && page.descriptionId &&
                        <div><HtmlFormattedMessage descriptor={{ id: page.descriptionId }} /></div>
                        }
                    </div>
                    {!inquiryMode &&
                    <Divider className={classes.pageDivider} />
                    }
                </div>
                {page.sections?.map((it, idx) => (
                    <ConditionalLogicCheck key={idx}
                        conditions={it.conditions}
                        content={it}
                        contentType={CONSTANTS.appTemplateControlTypes.section}
                        pageId={page.id}
                    >
                        <div>
                            <Typography component="div">
                                <div className={sectionHeadingClassName}>
                                    {it.showTitle && it.titleId &&
                                    <>
                                        <SectionLabel>
                                            <FormattedMessage id={it.titleId} />
                                        </SectionLabel>
                                    </>
                                    }
                                    {it.showHelp && it.helpId &&
                                    <>
                                        <IconButton
                                            aria-label={intl.formatMessage({ id: 'helpText' })}
                                            color="gray"
                                            onClick={handleSectionHelpClick}
                                        >
                                            <Icon name="help" />
                                        </IconButton>
                                        <Popper
                                            anchorEl={sectionHelpAnchorEl}
                                            arrow
                                            onClickAway={() => setOpenSectionHelp(false)}
                                            open={openSectionHelp}
                                            placement={isMobile ? 'bottom' : 'right'}
                                            text={intl.formatMessage({ id: it.helpId })}
                                            transition={false}
                                        />
                                    </>
                                    }
                                </div>
                                {
                                    it.showDescription && it.descriptionId && it.descriptionTop === 'true' &&
                                <div><HtmlFormattedMessage descriptor={{ id: it.descriptionId }} /></div>
                                }
                                {!it.showRepetition &&
                                <QuestionGroups
                                    pageId={page.id}
                                    groups={page.sections[idx].groups}
                                    prefix={page.id}
                                />
                                }
                                {it.showRepetition &&
                                <RepeatingSection name={`${page.id}.${it.id}`} section={it}>
                                    {(name) => {
                                        return (
                                            <QuestionGroups
                                                groups={page.sections[idx].groups}
                                                prefix={name}
                                                repeatingSection={true}
                                                pageId={page.id}
                                                sectionId={it.id}
                                            />
                                        );
                                    }}
                                </RepeatingSection>
                                }
                                {
                                    it.showDescription && it.descriptionId && it.descriptionTop === 'false' &&
                                <div><HtmlFormattedMessage descriptor={{ id: it.descriptionId }} /></div>
                                }
                            </Typography>
                        </div>
                    </ConditionalLogicCheck>
                ))}
            </main>
        </div>
    );
};
ApplicationFormPage.propTypes = {
    page: PropTypes.object.isRequired,
};
export default ApplicationFormPage;
